import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from './ui/Icons';
import { AppHeader } from './ui/AppHeader';
import { UserProfile, Organization, MediaItem, MediaType } from '../types';
import { getOrgMedia, deleteMediaItem, getUserProfile } from '../services/firebase';

interface MediaManagerProps {
  user: UserProfile;
  organization: Organization | null;
  onLogout: () => void;
  onSelectMedia?: (media: MediaItem) => void;
  pickerMode?: boolean;
  allowedTypes?: MediaType[];
}

export const MediaManager: React.FC<MediaManagerProps> = ({
  user,
  organization,
  onLogout,
  onSelectMedia,
  pickerMode = false,
  allowedTypes
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('admin');
  const [loading, setLoading] = useState(true);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [filterType, setFilterType] = useState<MediaType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [deleteConfirm, setDeleteConfirm] = useState<MediaItem | null>(null);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadMedia();
  }, [organization, filterType]);

  const loadMedia = async () => {
    if (!organization) return;

    setLoading(true);
    try {
      const typeFilter = filterType === 'all' ? undefined : filterType;
      const items = await getOrgMedia(organization.id, typeFilter);
      setMediaItems(items);

      // Fetch user names for all unique uploaders
      const uniqueUserIds = [...new Set(items.map(m => m.uploadedBy))];
      const names: Record<string, string> = {};
      await Promise.all(
        uniqueUserIds.map(async (userId) => {
          try {
            const profile = await getUserProfile(userId);
            names[userId] = profile?.displayName || 'Unknown';
          } catch {
            names[userId] = 'Unknown';
          }
        })
      );
      setUserNames(names);
    } catch (error) {
      console.error('Failed to load media:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (media: MediaItem) => {
    setDeleting(true);
    try {
      await deleteMediaItem(media.id, media.storagePath);
      setMediaItems(prev => prev.filter(m => m.id !== media.id));
      setDeleteConfirm(null);
      setSelectedMedia(null);
    } catch (error) {
      console.error('Failed to delete media:', error);
      alert('Failed to delete media. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterType]);

  const filteredMedia = mediaItems.filter(item => {
    if (allowedTypes && !allowedTypes.includes(item.mediaType)) return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.prompt?.toLowerCase().includes(query) ||
      item.fileName?.toLowerCase().includes(query)
    );
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredMedia.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMedia = filteredMedia.slice(startIndex, startIndex + itemsPerPage);

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getTypeIcon = (type: MediaType) => {
    switch (type) {
      case 'image': return <Icons.Image className="w-4 h-4" />;
      case 'video': return <Icons.Youtube className="w-4 h-4" />;
      case 'audio': return <Icons.Audio className="w-4 h-4" />;
    }
  };

  const getTypeBadgeColors = (type: MediaType) => {
    switch (type) {
      case 'image': return 'bg-purple-100 text-purple-700';
      case 'video': return 'bg-red-100 text-red-700';
      case 'audio': return 'bg-blue-100 text-blue-700';
    }
  };

  const renderMediaPreview = (media: MediaItem, isModal: boolean = false) => {
    const containerClass = isModal ? 'w-full rounded-lg' : 'w-full h-40 object-cover rounded-t-xl';

    switch (media.mediaType) {
      case 'image':
        return (
          <img
            src={media.url}
            alt={media.prompt || 'Media'}
            className={containerClass}
          />
        );
      case 'video':
        return isModal ? (
          <video src={media.url} controls className="w-full rounded-lg" />
        ) : (
          <div className="w-full h-40 bg-black rounded-t-xl flex items-center justify-center relative overflow-hidden">
            <video src={media.url} className="w-full h-full object-cover" muted />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <Icons.Play className="w-10 h-10 text-white/80" />
            </div>
          </div>
        );
      case 'audio':
        return isModal ? (
          <audio src={media.url} controls className="w-full" />
        ) : (
          <div className="w-full h-40 bg-gradient-to-br from-blue-500 to-blue-600 rounded-t-xl flex items-center justify-center">
            <Icons.Audio className="w-12 h-12 text-white/80" />
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {!pickerMode && (
        <AppHeader
          user={user}
          organization={organization}
          onLogout={onLogout}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      )}

      <div className={`max-w-6xl mx-auto px-6 ${pickerMode ? 'py-4' : 'py-8'}`}>
        {/* Header */}
        {!pickerMode && (
          <>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
            >
              <Icons.ChevronLeft className="w-5 h-5" />
              Back to Dashboard
            </button>

            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Media Library</h1>
              <p className="text-gray-600">
                Manage all generated and uploaded media for {organization?.name}
              </p>
            </div>
          </>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Icons.Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by prompt or filename..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none bg-white"
            />
          </div>

          {!allowedTypes && (
            <div className="flex gap-2">
              {(['all', 'image', 'video', 'audio'] as const).map(type => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    filterType === type
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {type === 'all' ? 'All' : type.charAt(0).toUpperCase() + type.slice(1) + 's'}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Media Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Icons.Loader className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredMedia.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
            <Icons.Image className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No media found</h3>
            <p className="text-gray-500">
              {searchQuery
                ? 'Try adjusting your search query'
                : 'AI-generated images, videos, and audio will appear here'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedMedia.map(media => (
                <div
                  key={media.id}
                  onClick={() => pickerMode && onSelectMedia ? onSelectMedia(media) : setSelectedMedia(media)}
                  className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all cursor-pointer ${
                    pickerMode ? 'hover:border-primary hover:ring-2 hover:ring-primary/20' : ''
                  }`}
                >
                  <div className="relative">
                    {renderMediaPreview(media)}
                    <div className="absolute top-2 right-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getTypeBadgeColors(media.mediaType)}`}>
                        {getTypeIcon(media.mediaType)}
                        {media.mediaType}
                      </span>
                    </div>
                    {media.isAIGenerated && (
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium flex items-center gap-1">
                          <Icons.Star className="w-3 h-3" />
                          AI
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    {media.prompt && (
                      <p className="text-sm text-gray-700 line-clamp-2 mb-2">
                        "{media.prompt}"
                      </p>
                    )}
                    {!media.prompt && media.sourceType && (
                      <p className="text-sm text-gray-500 mb-2 capitalize">
                        {media.sourceType.replace('-', ' ')}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{formatDate(media.uploadedAt)}</span>
                      <span>{userNames[media.uploadedBy] || 'Unknown'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredMedia.length)} of {filteredMedia.length} items
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="First page"
                  >
                    <Icons.ChevronsLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Previous page"
                  >
                    <Icons.ChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        // Show first, last, current, and pages around current
                        if (page === 1 || page === totalPages) return true;
                        if (Math.abs(page - currentPage) <= 1) return true;
                        return false;
                      })
                      .map((page, index, arr) => {
                        // Add ellipsis if there's a gap
                        const showEllipsisBefore = index > 0 && page - arr[index - 1] > 1;
                        return (
                          <React.Fragment key={page}>
                            {showEllipsisBefore && (
                              <span className="px-2 text-gray-400">...</span>
                            )}
                            <button
                              onClick={() => setCurrentPage(page)}
                              className={`min-w-[36px] h-9 rounded-lg font-medium text-sm transition-colors ${
                                currentPage === page
                                  ? 'bg-primary text-white'
                                  : 'border border-gray-200 hover:bg-gray-50 text-gray-700'
                              }`}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        );
                      })}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Next page"
                  >
                    <Icons.ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Last page"
                  >
                    <Icons.ChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Preview Modal */}
      {selectedMedia && !pickerMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Media Details</h3>
                <button
                  onClick={() => setSelectedMedia(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icons.X className="w-5 h-5" />
                </button>
              </div>

              {/* Full Preview */}
              <div className="mb-6">
                {renderMediaPreview(selectedMedia, true)}
              </div>

              {/* Metadata */}
              <div className="space-y-4 text-sm">
                {selectedMedia.prompt && (
                  <div>
                    <span className="text-gray-500 font-medium">Prompt:</span>
                    <p className="text-gray-900 mt-1">{selectedMedia.prompt}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-500 font-medium">Type:</span>
                    <p className="text-gray-900 capitalize">{selectedMedia.mediaType}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 font-medium">Source:</span>
                    <p className="text-gray-900 capitalize">{selectedMedia.sourceType.replace('-', ' ')}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 font-medium">Created:</span>
                    <p className="text-gray-900">{formatDate(selectedMedia.uploadedAt)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 font-medium">By:</span>
                    <p className="text-gray-900">{userNames[selectedMedia.uploadedBy] || 'Unknown'}</p>
                  </div>
                  {selectedMedia.dimensions && (
                    <div>
                      <span className="text-gray-500 font-medium">Dimensions:</span>
                      <p className="text-gray-900">{selectedMedia.dimensions.width} x {selectedMedia.dimensions.height}</p>
                    </div>
                  )}
                  {selectedMedia.duration && (
                    <div>
                      <span className="text-gray-500 font-medium">Duration:</span>
                      <p className="text-gray-900">{selectedMedia.duration}s</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6 pt-6 border-t border-gray-100">
                <button
                  onClick={() => setDeleteConfirm(selectedMedia)}
                  className="px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 flex items-center gap-2 transition-colors"
                >
                  <Icons.Trash className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Icons.Trash className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Delete Media?</h3>
            </div>
            <p className="text-gray-600 mb-6">
              This will permanently delete this {deleteConfirm.mediaType} from storage. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              >
                {deleting ? (
                  <Icons.Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Icons.Trash className="w-4 h-4" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
